import 'whatwg-fetch'
import Promise from 'promise-polyfill'
import ReactGA from 'react-ga4'
import * as Sentry from '@sentry/react'

import AppStore from '../stores/AppStore'
import { connectionIssues } from './errors'
import { buildURL, getWithAuth, sendWithAuth } from '../util/api'

if (!window.Promise) {
  window.Promise = Promise
}

export function getUser (userID, callback) {
  const url = buildURL('/users/authID')

  fetch(url, getWithAuth())
    .then((response) => {
      if (response.status === 200) {
        connectionIssues(null, 'Connected')
        return response.json()
      }

      if (response.status >= 400 && response.status < 500) {
        throw new Error('Login Server Request Failed')
      }

      if (response.status >= 500 || response.issue === 'Error fetching data') {
        throw new Error('Server Error')
      }

      throw new Error(`Bad status code: ${response.status}`)
    }, () => {
      throw new Error('Issue connecting to server')
    })
    .then((data) => {
      const {
        id,
        customer_id,
        locale,
        permissions,
        onboarding,
      } = data

      AppStore.data.user = {
        ...AppStore.data.user,
        data,
        id,
        customer_id,
        locale,
        permissions,
        onboarding,
        ready: true,
      }

      // Emit change
      AppStore.emitChange()

      if (import.meta.env.PROD) {
        ReactGA.set({ user_id: data.id })
        Sentry.setUser({ id: data.id, email: data.email })
      }

      // Trigger callback (from server)
      if (callback) {
        callback(null, AppStore)
      }
    })
    .catch((error) => {
      if (callback) {
        callback(error)
      }

      const errorStr = error.toString()

      if (errorStr === 'Error: Login Server Request Failed') {
        return connectionIssues({ issue: 'Unauthorized', error })
      }

      if (
        errorStr === 'Error: Server Error' ||
        errorStr === 'Error: Issue connecting to server'
      ) {
        return connectionIssues({ issue: 'Server Error', error })
      }

      return connectionIssues({ issue: 'Error fetching data', error })
    })
}

export function getCustomer (customerID, callback) {
  const url = buildURL(`/customers/${customerID}`)

  fetch(url, getWithAuth())
    .then((response) => {
      if (response.status === 200) {
        connectionIssues(null, 'Connected')
        return response.json()
      }
      throw new Error(`Bad status code: ${response.status}`)
    }, () => {
      throw new Error('Issue connecting to server')
    })
    .then((data) => {
      AppStore.data.customer.data = data
      AppStore.data.customer.ready = true
      AppStore.data.customer.changing = false
      AppStore.data.groups = data.applications
      AppStore.data.groupsReady = true

      // Emit change
      AppStore.emitChange()

      // Trigger callback (from server)
      if (callback) {
        callback(null, AppStore)
      }
    })
    .catch((error) => {
      connectionIssues({ issue: 'Error fetching data', error })
      if (callback) {
        callback(error)
      }
    })
}

export function changePassword (payload, token, callback) {
  fetch(
    buildURL('/users/passwordReset'),
    {
      method: 'put',
      body: JSON.stringify(payload),
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  )
    .then((response) => {
      if (response.status === 200) {
        connectionIssues(null, 'Connected')
        return
      }

      throw new Error(`Bad status code: ${response.status}`)
    },
    () => { throw new Error('Issue connecting to server') })
    .then((data) => {
      if (callback) {
        callback(null, data)
      }
    })
    .catch((error) => {
      connectionIssues({ issue: 'Error fetching data', error })
      if (callback) {
        callback(error)
      }
    })
}

export function parseJwt (token) {
  const base64Url = token.split('.')[1]
  const base64 = base64Url.replace('-', '+').replace('_', '/')
  return JSON.parse(window.atob(base64))
}

export function inviteUser (payload, callback) {
  const url = buildURL('/users/invite')

  fetch(url, sendWithAuth(payload))
    .then((response) => {
      if (response.status === 200) {
        connectionIssues(null, 'Connected')
        return response.json()
      }

      // eslint-disable-next-line no-throw-literal
      throw {
        name: 'Unexpected status',
        error: `Bad status code: ${response.status}`,
        status: response.status,
        statusText: response.statusText,
      }
    }, (error) => {
      throw new Error({
        error,
        status: 500,
        statusText: 'Issue connecting to server',
      })
    })
    .then((data) => {
      if (callback) {
        callback(null, data)
      }
    })
    .catch((error) => {
      console.error(error)
      if (error.status === 422 || error.status === 409) {
        connectionIssues({ issue: 'User already exists', error })
      } else if (error.status !== 409) {
        connectionIssues({ issue: 'Error fetching data', error })
      }
      if (callback) {
        callback(error)
      }
    })
}

export function updateUser (userId, change, callback) {
  const url = buildURL(`/users/${userId}`)

  const existingLocale = AppStore.data.user?.data?.locale || {}

  // Normalize locale changes: merge with existing locale and wrap top-level locale fields if needed
  let normalizedChange = change
  if (change && change.locale) {
    normalizedChange = {
      ...change,
      locale: {
        ...existingLocale,
        ...change.locale,
      },
    }
  } else if (change && (
    Object.prototype.hasOwnProperty.call(change, 'unitsOfVolume') ||
    Object.prototype.hasOwnProperty.call(change, 'unitsOfFlow') ||
    Object.prototype.hasOwnProperty.call(change, 'tempConv') ||
    Object.prototype.hasOwnProperty.call(change, 'language')
  )) {
    normalizedChange = {
      locale: {
        ...existingLocale,
        ...change,
      },
    }
  }

  fetch(url, sendWithAuth(normalizedChange, true))
    .then((response) => {
      if (response.status === 200) {
        if (normalizedChange?.locale) {
          const mergedLocale = { ...existingLocale, ...normalizedChange.locale }
          if (AppStore.data.user?.data) {
            AppStore.data.user.data = {
              ...AppStore.data.user.data,
              locale: mergedLocale,
            }
          }
          AppStore.data.user.locale = mergedLocale
        }
        AppStore.data.user.saved = true
        AppStore.emitChange()
        // Trigger callback (from server)
        getUser()
        if (callback) {
          callback(null)
        }
        connectionIssues(null, 'Connected')
      } else {
        throw new Error(`Bad status code: ${response.status}`)
      }
    }, () => {
      throw new Error('Issue connecting to server')
    })
    .catch((error) => {
      AppStore.data.user.saved = 'error'
      AppStore.emitChange()
      if (callback) {
        callback(error)
      }
      connectionIssues({ issue: 'Error fetching data', error })
    })
}

export function unloadUserChange () {
  AppStore.data.user.saved = false
  AppStore.emitChange()
}
